import {
  BulkDeleteDialog,
  BulkSelectionToolbar,
  formatBulkCount,
  useBulkDelete,
} from '@/components/app/bulk-delete';
import { ListPagination } from '@/components/app/list-page';
import { MobilePageShell } from '@/components/app/mobile-page-shell';
import { ImportDataDialog, useAppImporter } from '@/features/imports';
import { useListOffers } from '@/features/offers';
import {
  bulkDeleteBranchNote,
  useActiveLocation,
} from '@/features/organization-locations';
import {
  useDeleteService,
  useListServices,
  useUpdateService,
} from '@/features/organization-services';
import type { OrganizationService } from '@/features/organization-services';
import { useListPractitioners } from '@/features/practitioners';
import { useListCategories } from '@/features/service-categories';
import { BookingPaymentWarningBanner } from '@/features/stripe-connect/components/booking-payment-warning-banner';
import { ROUTES } from '@/lib/route-paths';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AssignServicesToTeamButton,
  AssignServicesToTeamDialog,
} from '../assign-services-to-team';
import { ImportServicesDialog } from '../import-services-dialog';
import { useServicePagination } from '../use-service-pagination';

import { ServicesMobileFilters } from './services-mobile-filters';
import { ServicesMobileList } from './services-mobile-list';
import {
  ALL_CATEGORIES,
  ALL_STAFF,
  type CategoryFilterValue,
  type StaffFilterValue,
  buildOffersByServiceId,
  buildPractitionersByService,
  filterServices,
  getCategoryFilterOptions,
  getStaffFilterOptions,
  groupServicesForMobileList,
} from './services-mobile-utils';

export function ServicesMobilePage() {
  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    void navigate({ to: ROUTES.dashboardAccount });
  }, [navigate]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] =
    useState<CategoryFilterValue>(ALL_CATEGORIES);
  const [staffFilter, setStaffFilter] = useState<StaffFilterValue>(ALL_STAFF);
  // Active AND archived — see the note on the desktop page. Mobile has the
  // same import entry point in its `+` menu, so importing "as archived" here
  // hid the rows just as thoroughly. Archived rows are badged and dimmed in
  // `ServicesMobileList`, so they can never be mistaken for bookable ones.
  const { services, isLoading, isError, error } = useListServices({
    isActive: undefined,
  });
  const { offers } = useListOffers({ limit: 100 });
  const { categories } = useListCategories();
  const { practitioners } = useListPractitioners({ params: { limit: 100 } });
  const { deleteService } = useDeleteService();
  const { deleteServiceAsync: deleteServiceQuietly } = useDeleteService({
    showSuccessToast: false,
    showErrorToast: false,
  });
  const { updateService } = useUpdateService({ showSuccessToast: false });

  const practitionersByService = useMemo(
    () => buildPractitionersByService(practitioners),
    [practitioners]
  );
  const offersByServiceId = useMemo(
    () => buildOffersByServiceId(offers),
    [offers]
  );

  const categoryOptions = useMemo(
    () => getCategoryFilterOptions(services, categories),
    [services, categories]
  );
  const staffOptions = useMemo(
    () => getStaffFilterOptions(practitioners),
    [practitioners]
  );

  const filteredServices = useMemo(
    () =>
      filterServices({
        services,
        search,
        categoryFilter,
        staffFilter,
        practitionersByService,
      }),
    [services, search, categoryFilter, staffFilter, practitionersByService]
  );

  const { rows, pagination } = useServicePagination(
    filteredServices,
    JSON.stringify([search, categoryFilter, staffFilter])
  );

  const { location, locations, isMultiLocation } = useActiveLocation();
  const rowIds = useMemo(() => rows.map((service) => service.id), [rows]);
  const bulk = useBulkDelete({
    deleteOne: deleteServiceQuietly,
    noun: { singular: 'service', plural: 'services' },
    rowIds,
    labelFor: (id) => services.find((service) => service.id === id)?.name,
    resetKey: `${search}|${categoryFilter}|${staffFilter}|${pagination.pageIndex}|${pagination.pageSize}|${location?.id ?? ''}`,
  });
  const bulkBranchNote = bulkDeleteBranchNote({
    records: services.filter((service) => bulk.selected.has(service.id)),
    totalBranches: locations.length,
  });
  const [assignOpen, setAssignOpen] = useState(false);
  const selectedServices = useMemo(
    () =>
      rows
        .filter((service) => bulk.selected.has(service.id))
        .map((service) => ({ id: service.id, name: service.name })),
    [rows, bulk.selected]
  );

  const groups = useMemo(
    () =>
      groupServicesForMobileList({
        services: rows,
        offersByServiceId,
        categories,
      }),
    [rows, offersByServiceId, categories]
  );

  const [importOpen, setImportOpen] = useState(false);
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const importer = useAppImporter();

  const openAddService = () => {
    void navigate({ to: '/create/$entity', params: { entity: 'service' } });
  };

  const openEditService = (service: OrganizationService) => {
    void navigate({
      to: '/edit/$entity/$id',
      params: { entity: 'service', id: service.id },
    });
  };

  const toggleArchive = (service: OrganizationService) => {
    const restoring = !service.isActive;
    updateService(
      { id: service.id, isActive: restoring },
      {
        onSuccess: () =>
          toast.success(restoring ? 'Service restored' : 'Service archived'),
      }
    );
  };

  return (
    <MobilePageShell
      onBack={handleBack}
      title="Services"
      toolbar={
        <div className="space-y-3">
          <ServicesMobileFilters
            categoryFilter={categoryFilter}
            categoryOptions={categoryOptions}
            onAddService={openAddService}
            onCategoryFilterChange={setCategoryFilter}
            onImportServices={
              isMultiLocation ? () => setImportOpen(true) : undefined
            }
            onImportServicesCsv={() => setImportCsvOpen(true)}
            onSearchChange={setSearch}
            onStaffFilterChange={setStaffFilter}
            search={search}
            staffFilter={staffFilter}
            staffOptions={staffOptions}
          />
          {bulk.hasSelection && (
            <BulkSelectionToolbar
              actions={
                <AssignServicesToTeamButton
                  disabled={bulk.isDeleting}
                  onClick={() => setAssignOpen(true)}
                  variant="mobile"
                />
              }
              bulk={bulk}
              variant="mobile"
            />
          )}
        </div>
      }
    >
      <ImportDataDialog
        importer={importer}
        kind="services"
        onOpenChange={setImportCsvOpen}
        open={importCsvOpen}
      />

      <ImportServicesDialog onOpenChange={setImportOpen} open={importOpen} />

      <AssignServicesToTeamDialog
        onAssigned={bulk.clear}
        onOpenChange={setAssignOpen}
        open={assignOpen}
        services={selectedServices}
      />

      <BulkDeleteDialog
        bulk={bulk}
        description={
          <>
            This permanently removes {formatBulkCount(bulk.count, bulk.noun)}{' '}
            from your catalog and from your public booking page. Appointments
            already booked are not affected.
            {bulkBranchNote && ` ${bulkBranchNote}`}
          </>
        }
      />

      <div className="px-4 pt-4 pb-1 empty:hidden">
        <BookingPaymentWarningBanner />
      </div>

      <ServicesMobileList
        errorMessage={error?.message}
        groups={groups}
        isError={isError}
        isLoading={isLoading}
        onAddService={openAddService}
        onDeleteService={(id) => deleteService(id)}
        onEditService={openEditService}
        onToggleArchive={toggleArchive}
        selection={bulk.selection}
      />
      {!isLoading && !isError && (
        <div className="p-4">
          <ListPagination {...pagination} />
        </div>
      )}
    </MobilePageShell>
  );
}
